import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CreateNetworkRuleRequest,
  NetworkRuleModel,
  UpdateNetworkRuleRequest,
} from '@app/core/models/partner/network-rule.model';

@Injectable({
  providedIn: 'root',
})
export class NetworkRuleService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/network-rules`;
  }

  getByNetworkId(networkId: string): Observable<NetworkRuleModel[]> {
    if (!networkId) return of([]);
    this.isLoading.set(true);
    return this.http.get<NetworkRuleModel[]>(`${this.baseUrl}/by-network/${networkId}`).pipe(
      map(list => (list ?? []).map(rule => this.normalizeRule(rule))),
      catchError(error => {
        console.error('Ошибка при получении правил сети:', error);
        this.translate.get('Ошибка при получении правил сети').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getAll(): Observable<NetworkRuleModel[]> {
    this.isLoading.set(true);
    return this.http.get<NetworkRuleModel[]>(this.baseUrl).pipe(
      map(list => (list ?? []).map(rule => this.normalizeRule(rule))),
      catchError(error => {
        console.error('Ошибка при получении правил сети:', error);
        this.translate.get('Ошибка при получении правил сети').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getById(id: string): Observable<NetworkRuleModel | null> {
    if (!id) return of(null);
    this.isLoading.set(true);
    return this.http.get<NetworkRuleModel>(`${this.baseUrl}/${id}`).pipe(
      map(rule => this.normalizeRule(rule)),
      catchError(error => {
        console.error('Ошибка при получении правила сети:', error);
        this.translate.get('Ошибка при получении правила сети').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  create(body: CreateNetworkRuleRequest): Observable<NetworkRuleModel> {
    this.isLoading.set(true);
    return this.http.post<NetworkRuleModel>(this.baseUrl, body).pipe(
      map(rule => this.normalizeRule(rule)),
      catchError(error => {
        console.error('Ошибка при создании правила сети:', error);
        const msg = error?.error?.message;
        if (msg) {
          this.snackbarService.error(msg);
        } else {
          this.translate.get('Ошибка при создании правила сети').subscribe(m => {
            this.snackbarService.error(m);
          });
        }
        return throwError(() => error);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(id: string, body: UpdateNetworkRuleRequest): Observable<NetworkRuleModel> {
    this.isLoading.set(true);
    return this.http.put<NetworkRuleModel>(`${this.baseUrl}/${id}`, body).pipe(
      map(rule => this.normalizeRule(rule)),
      catchError(error => {
        console.error('Ошибка при обновлении правила сети:', error);
        const msg = error?.error?.message;
        if (msg) {
          this.snackbarService.error(msg);
        } else {
          this.translate.get('Ошибка при обновлении правила сети').subscribe(m => {
            this.snackbarService.error(m);
          });
        }
        return throwError(() => error);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении правила сети:', error);
        this.translate.get('Ошибка при удалении правила сети').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return throwError(() => error);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activate(id: string): Observable<NetworkRuleModel> {
    if (!id) return throwError(() => new Error('Network rule id is required'));
    this.isLoading.set(true);
    return this.http.patch<NetworkRuleModel>(`${this.baseUrl}/${id}/activate`, {}).pipe(
      map(rule => this.normalizeRule(rule)),
      catchError(error => {
        console.error('Ошибка при активации правила сети:', error);
        this.translate.get('Ошибка при активации правила сети').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return throwError(() => error);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivate(id: string): Observable<NetworkRuleModel> {
    if (!id) return throwError(() => new Error('Network rule id is required'));
    this.isLoading.set(true);
    return this.http.patch<NetworkRuleModel>(`${this.baseUrl}/${id}/deactivate`, {}).pipe(
      map(rule => this.normalizeRule(rule)),
      catchError(error => {
        console.error('Ошибка при деактивации правила сети:', error);
        this.translate.get('Ошибка при деактивации правила сети').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return throwError(() => error);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  private normalizeRule(rule: NetworkRuleModel): NetworkRuleModel {
    const ranges = rule.saleRanges ?? rule.ranges ?? [];
    return {
      ...rule,
      ranges,
      saleRanges: ranges,
    };
  }
}
